import React, { useState, useEffect } from 'react';
import { ChevronDown, Globe } from 'lucide-react';
import { ParticleText } from '@/components/ui/ParticleText';

interface KeyInsight {
  status: 'normal' | 'abnormal';
  title: string;
  metric_name: string;
  value: string;
  reference_range?: string;
  description: string;
}

interface AIClinicalAnalysis {
  overall_headline: string;
  generated_at: string;
  key_insights: KeyInsight[];
  recommended_actions: string[];
}

type LanguageCode = 'en' | 'hi' | 'ml';

interface LanguageOption {
  code: LanguageCode;
  name: string;
  nativeName: string;
}

const LANGUAGES: LanguageOption[] = [
  { code: 'en', name: 'English', nativeName: 'English' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिंदी' },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം' },
];

interface AIAnalysisSectionProps {
  summary: string | null;
  clinicalAnalysis?: AIClinicalAnalysis | null;
  isSkeleton?: boolean;
  // Translation props
  translationsReady?: boolean;
  currentLanguage?: LanguageCode;
  preferredLanguage?: LanguageCode;
  onLanguageChange?: (language: LanguageCode) => void;
  isChangingLanguage?: boolean;
}

export const AIAnalysisSection: React.FC<AIAnalysisSectionProps> = ({
  summary,
  clinicalAnalysis,
  isSkeleton = false,
  translationsReady = false,
  currentLanguage = 'en',
  preferredLanguage = 'en',
  onLanguageChange,
  isChangingLanguage = false,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Debug logging
  useEffect(() => {
    console.log('[AIAnalysisSection] 🌐 Translation state:', {
      translationsReady,
      currentLanguage,
      preferredLanguage,
      isChangingLanguage,
      hasClinicalAnalysis: !!clinicalAnalysis,
    });
  }, [translationsReady, currentLanguage, preferredLanguage, isChangingLanguage, clinicalAnalysis]);

  const handleLanguageSelect = (langCode: LanguageCode) => {
    console.log('[AIAnalysisSection] 🌐 Language selected:', langCode);
    setIsDropdownOpen(false);
    if (onLanguageChange && langCode !== currentLanguage) {
      onLanguageChange(langCode);
    }
  };

  const getLanguageStatus = (langCode: LanguageCode): 'ready' | 'translating' | 'current' => {
    if (langCode === currentLanguage) return 'current';
    if (langCode === 'en') return 'ready'; // English is always ready
    return translationsReady ? 'ready' : 'translating';
  };

  const currentLangOption = LANGUAGES.find(l => l.code === currentLanguage) || LANGUAGES[0];

  const formatDate = (isoDate?: string) => {
    const date = isoDate ? new Date(isoDate) : new Date();
    const month = date.toLocaleString('en-US', { month: 'short' }).toUpperCase();
    const day = date.getDate();
    const time = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return `${month} ${day} · ${time}`;
  };

  // Show skeleton state if requested
  if (isSkeleton) {
    return (
      <div
        style={{
          width: '100%',
          maxWidth: '700px',
          background: 'var(--color-card-bg)',
          border: '1px solid var(--color-border)',
          borderRadius: '16px',
          padding: '32px',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '400px',
          opacity: 0.5,
          filter: 'grayscale(1)',
          pointerEvents: 'none'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div style={{ width: '120px', height: '10px', background: 'rgba(0,0,0,0.05)', borderRadius: '4px' }} />
          <div style={{ width: '80px', height: '10px', background: 'rgba(0,0,0,0.05)', borderRadius: '4px' }} />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem', justifyContent: 'center' }}>
          <div style={{ width: '100%', height: '20px', background: 'rgba(0,0,0,0.05)', borderRadius: '4px' }} />
          <div style={{ width: '80%', height: '20px', background: 'rgba(0,0,0,0.05)', borderRadius: '4px' }} />
          <div style={{ width: '90%', height: '20px', background: 'rgba(0,0,0,0.05)', borderRadius: '4px' }} />
        </div>
      </div>
    );
  }

  // If no clinical analysis, show empty state with consistent styling
  if (!clinicalAnalysis) {
    return (
      <div
        style={{
          width: '100%',
          maxWidth: '700px',
          background: 'var(--color-card-bg)',
          border: '1px solid var(--color-border)',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
          borderRadius: '16px',
          padding: '32px',
          display: 'flex',
          flexDirection: 'column',
          minHeight: '400px'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '11px',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: 'var(--color-secondary)',
          marginBottom: '24px'
        }}>
          <span>AI: Health Summary</span>
          <span>{formatDate()}</span>
        </div>

        {/* Main Content */}
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          padding: '40px 20px'
        }}>
          <div style={{
            fontSize: '16px',
            color: 'var(--color-secondary)',
            lineHeight: '1.6',
            maxWidth: '500px'
          }}>
            {summary || 'Upload your health reports to receive AI-powered clinical analysis and personalized health insights from our virtual doctor.'}
          </div>
        </div>

        {/* Signature */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '20px',
          paddingTop: '20px',
          borderTop: '1px solid var(--color-border)'
        }}>
          <span style={{
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            fontSize: '20px',
            color: 'var(--color-text)'
          }}>
            — Dr. AI
          </span>
        </div>
      </div>
    );
  }

  // New structured clinical analysis card
  return (
    <div
      className="clinical-analysis-card"
      style={{
        width: '100%',
        maxWidth: '700px',
        maxHeight: '600px',
        background: 'var(--color-card-bg)',
        border: '1px solid var(--color-border)',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
        borderRadius: '16px',
        padding: '32px',
        transition: 'all 0.4s ease',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'visible'
      }}
    >
      {/* Custom Scrollbar Styles */}
      <style>{`
        .clinical-analysis-card::-webkit-scrollbar {
          width: 8px;
        }

        .clinical-analysis-card::-webkit-scrollbar-track {
          background: var(--color-scrollbar-track);
          border-radius: 10px;
        }

        .clinical-analysis-card::-webkit-scrollbar-thumb {
          background: var(--color-scrollbar-thumb);
          border-radius: 10px;
          transition: background 0.3s ease;
        }

        .clinical-analysis-card::-webkit-scrollbar-thumb:hover {
          background: var(--color-scrollbar-thumb-hover);
        }

        .insight-item {
          padding: 12px;
          margin-left: -12px;
          margin-right: -12px;
          border-radius: 8px;
          transition: background 0.2s ease;
        }

        .insight-item:hover {
          background: var(--color-hover-bg);
        }
      `}</style>

      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingBottom: '16px',
        borderBottom: '1px solid var(--color-border)',
        marginBottom: '24px'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <span style={{
            fontSize: '11px',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: 'var(--color-secondary)',
            fontWeight: 600
          }}>
            AI: Health Summary
          </span>
          <span style={{ fontSize: '10px', color: 'var(--color-muted)' }}>
            {formatDate(clinicalAnalysis.generated_at)}
          </span>
        </div>

        {/* Language Dropdown */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            disabled={isChangingLanguage}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 10px',
              fontSize: '11px',
              fontWeight: 500,
              color: 'var(--color-secondary)',
              background: 'var(--color-hover-bg)',
              border: '1px solid var(--color-border)',
              borderRadius: '6px',
              cursor: isChangingLanguage ? 'wait' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: isChangingLanguage ? 0.7 : 1,
            }}
          >
            <Globe size={12} />
            <span>{currentLangOption.nativeName}</span>
            {isChangingLanguage ? (
              <span style={{
                width: '10px',
                height: '10px',
                border: '2px solid var(--color-border)',
                borderTopColor: 'var(--color-accent-blue)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            ) : (
              <ChevronDown size={12} style={{
                transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease'
              }} />
            )}
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && !isChangingLanguage && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '4px',
              background: 'var(--color-card-bg)',
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 100,
              minWidth: '140px',
              overflow: 'hidden',
            }}>
              {LANGUAGES.map((lang) => {
                const status = getLanguageStatus(lang.code);
                const isDisabled = status === 'translating';
                const isCurrent = lang.code === currentLanguage;

                return (
                  <button
                    key={lang.code}
                    onClick={() => !isDisabled && handleLanguageSelect(lang.code)}
                    disabled={isDisabled}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      padding: '10px 12px',
                      fontSize: '12px',
                      color: isDisabled ? 'var(--color-muted)' : 'var(--color-text)',
                      background: isCurrent ? 'var(--color-hover-bg)' : 'transparent',
                      border: 'none',
                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                      transition: 'background 0.15s ease',
                      textAlign: 'left',
                    }}
                    onMouseEnter={(e) => {
                      if (!isDisabled && !isCurrent) {
                        e.currentTarget.style.background = 'var(--color-hover-bg)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isCurrent) {
                        e.currentTarget.style.background = 'transparent';
                      }
                    }}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>{lang.nativeName}</span>
                      <span style={{ fontSize: '10px', color: 'var(--color-muted)' }}>
                        ({lang.name})
                      </span>
                    </span>
                    {isDisabled && (
                      <span style={{
                        fontSize: '9px',
                        color: 'var(--color-accent-blue)',
                        fontStyle: 'italic'
                      }}>
                        Translating...
                      </span>
                    )}
                    {isCurrent && !isDisabled && (
                      <span style={{ color: 'var(--color-accent-blue)' }}>✓</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Spinner animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Scrolled Content Wrapper */}
      <div
        className="clinical-analysis-content"
        style={{
          flex: 1,
          overflowY: 'auto',
          marginRight: '-16px',
          paddingRight: '16px',
          marginTop: '4px'
        }}
      >
        <style>{`
          .clinical-analysis-content::-webkit-scrollbar {
            width: 6px;
          }
          .clinical-analysis-content::-webkit-scrollbar-track {
            background: transparent;
          }
          .clinical-analysis-content::-webkit-scrollbar-thumb {
            background: var(--color-border);
            border-radius: 10px;
          }
        `}</style>

        {/* Main Headline */}
        <div style={{
          fontSize: '22px',
          fontWeight: 600,
          marginBottom: '24px',
          letterSpacing: '-0.02em',
          color: 'var(--color-text)'
        }}>
          <ParticleText
            isLoading={isChangingLanguage}
            text={clinicalAnalysis.overall_headline}
            fontSize={22}
            fontWeight={600}
          />
        </div>

        {/* Key Insights - Single Column */}
        <div style={{
          marginBottom: '24px'
        }}>
          {clinicalAnalysis.key_insights.map((insight, index) => (
            <div
              key={index}
              className="insight-item"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                marginBottom: '16px'
              }}
            >
              {/* Status Dot */}
              <div
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  marginTop: '6px',
                  flexShrink: 0,
                  background: insight.status === 'normal' ? '#27ae60' : '#e74c3c',
                  boxShadow: insight.status === 'normal'
                    ? '0 0 8px rgba(39, 174, 96, 0.4)'
                    : '0 0 8px rgba(231, 76, 60, 0.4)',
                  transition: 'background-color 0.3s ease'
                }}
              />

              <div style={{ flex: 1 }}>
                {/* Insight Title with Badge */}
                <div style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  marginBottom: '4px',
                  color: 'var(--color-text)'
                }}>
                  <ParticleText
                    isLoading={isChangingLanguage}
                    text={insight.title}
                    fontSize={16}
                    fontWeight={700}
                  />
                  <span
                    style={{
                      display: 'inline-block',
                      fontSize: '10px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      marginLeft: '8px',
                      background: insight.status === 'normal'
                        ? 'rgba(39, 174, 96, 0.1)'
                        : 'rgba(231, 76, 60, 0.1)',
                      color: insight.status === 'normal' ? '#27ae60' : '#e74c3c'
                    }}
                  >
                    {insight.status === 'normal' ? 'Normal' : 'Abnormal'}
                  </span>
                </div>

                {/* Insight Description */}
                <div style={{
                  fontSize: '14px',
                  color: 'var(--color-secondary)',
                  lineHeight: '1.5'
                }}>
                  <ParticleText
                    isLoading={isChangingLanguage}
                    text={insight.description}
                    fontSize={14}
                    fontWeight={400}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recommended Actions */}
        {clinicalAnalysis.recommended_actions && clinicalAnalysis.recommended_actions.length > 0 && (
          <div style={{
            borderTop: '1px solid var(--color-border)',
            paddingTop: '24px',
            marginBottom: '24px'
          }}>
            <div style={{
              fontSize: '16px',
              fontWeight: 700,
              marginBottom: '12px',
              color: 'var(--color-text)'
            }}>
              Recommended Actions:
            </div>
            <ul style={{
              listStyle: 'none',
              padding: 0,
              margin: 0
            }}>
              {clinicalAnalysis.recommended_actions.map((action, index) => (
                <li
                  key={index}
                  style={{
                    position: 'relative',
                    paddingLeft: '20px',
                    fontSize: '14px',
                    marginBottom: '8px',
                    color: 'var(--color-text)'
                  }}
                >
                  <span style={{
                    position: 'absolute',
                    left: 0,
                    color: 'var(--color-accent-blue)'
                  }}>
                    •
                  </span>
                  <ParticleText
                    isLoading={isChangingLanguage}
                    text={action}
                    fontSize={14}
                    fontWeight={400}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Signature */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '20px',
          paddingTop: '20px',
          borderTop: '1px solid var(--color-border)'
        }}>
          <span style={{
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            fontSize: '20px',
            color: 'var(--color-text)'
          }}>
            — Dr. AI
          </span>
        </div>
      </div>
    </div>
  );
};
